import React, {useState} from "react";
import styled, {css, keyframes} from "styled-components";
import {useInventory} from "../State/InventoryContext";
import {CRAFTING_RECIPES} from "../State/GameData";
import {ItemIcon} from "../SharedComponents/ItemIcon";

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-8px); }
  to { opacity: 1; transform: translateY(0); }
`;

const warningBlink = keyframes`
  0%, 100% { background-color: #fff; }
  25%, 75% { background-color: #fff3cd; }
  50% { background-color: #ffeaa7; }
`;

const S = {
  Sidebar: styled.div`
    position: fixed;
    right: 0;
    top: 0;
    bottom: 0;
    width: 280px;
    background: linear-gradient(180deg, #fff9e6 0%, #fffef7 100%);
    border-left: 1px solid #e6d5a8;
    box-shadow: -2px 0 8px rgba(0,0,0,0.05);
    display: flex;
    flex-direction: column;
    z-index: 5;
  `,
  Header: styled.div`
    padding: 16px;
    border-bottom: 1px solid #e6d5a8;
    background: #fff;
  `,
  Title: styled.h3`
    margin: 0;
    font-size: 16px;
    color: #0b3d91;
    font-weight: 700;
  `,
  CountBadge: styled.span`
    background: #0d6efd;
    color: white;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 6px;
    border-radius: 10px;
    min-width: 24px;
    text-align: center;
    cursor: pointer;
    transition: background-color 0.15s ease;
    &:hover {
      background: #0b5ed7;
    }
  `,
  DragHandle: styled.div<{ $disabled: boolean }>`
    width: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: ${props => props.$disabled ? 'default' : 'grab'};
    color: ${props => props.$disabled ? '#d1d5db' : '#6b7280'};
    font-size: 16px;
    user-select: none;
    &:active {
      cursor: ${props => props.$disabled ? 'default' : 'grabbing'};
    }
  `,
  QueueList: styled.div`
    flex: 1;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  `,
  QueueItem: styled.div<{ $warning: boolean; $isDragging: boolean }>`
    background: white;
    border: 2px solid ${props => props.$warning ? '#ffc107' : '#e5e7eb'};
    border-radius: 10px;
    padding: 10px;
    animation: ${fadeIn} 300ms ease;
    ${props => props.$warning && css`animation: ${warningBlink} 800ms ease 2;`}
    cursor: ${props => props.$warning ? 'pointer' : 'default'};
    opacity: ${props => props.$isDragging ? 0.5 : 1};
    transition: opacity 0.2s ease;
    &:hover {
      ${props => props.$warning && 'background: #fffbf0;'}
    }
  `,
  ItemHeader: styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 8px;
  `,
  ItemName: styled.div`
    flex: 1;
    font-size: 13px;
    font-weight: 600;
    color: #1f2937;
  `,
  RemoveButton: styled.button`
    background: #ef4444;
    color: white;
    border: none;
    border-radius: 4px;
    width: 20px;
    height: 20px;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    font-size: 14px;
    &:hover { background: #dc2626; }
  `,
  ProgressBar: styled.div`
    height: 8px;
    background: #f3f4f6;
    border-radius: 4px;
    overflow: hidden;
  `,
  ProgressFill: styled.div<{ $progress: number }>`
    height: 100%;
    width: ${props => props.$progress}%;
    background: linear-gradient(90deg, #fbbf24, #f59e0b);
    transition: width 0.3s linear;
  `,
  WarningText: styled.div`
    margin-top: 4px;
    font-size: 11px;
    color: #d97706;
    font-weight: 600;
  `,
  EmptyState: styled.div`
    padding: 32px 16px;
    text-align: center;
    color: #9ca3af;
    font-size: 14px;
  `,
};

type QueueStack = {
  recipeId: string;
  count: number;
  items: { id: string; progressMs: number; missingIngredients: boolean }[];
  firstItem: { id: string; progressMs: number; missingIngredients: boolean };
  startIndex: number; // Index in original queue where this stack starts
};

export function CraftingQueueSidebar() {
  const {inventory, removeFromQueue, reorderQueue, queueMissingIngredients} = useInventory();
  const CRAFTING_POWER = 0.25;
  const [draggedStackIndex, setDraggedStackIndex] = useState<number | null>(null);

  const handleWarningClick = (itemId: string, stackCount: number) => {
    queueMissingIngredients(itemId, stackCount);
  };

  const handleRemoveOne = (stack: QueueStack, e: React.MouseEvent) => {
    e.stopPropagation();
    // Remove the last item from this stack (not the first which might be in progress)
    if (stack.items.length > 0) {
      const lastItem = stack.items[stack.items.length - 1];
      removeFromQueue(lastItem.id);
    }
  };

  // Group consecutive items with the same recipeId
  const groupedQueue: QueueStack[] = [];
  let currentIndex = 0;
  inventory.craftingQueue.forEach((item) => {
    const lastGroup = groupedQueue[groupedQueue.length - 1];
    if (lastGroup && lastGroup.recipeId === item.recipeId) {
      // Add to existing group
      lastGroup.count++;
      lastGroup.items.push(item);
    } else {
      // Create new group
      groupedQueue.push({
        recipeId: item.recipeId,
        count: 1,
        items: [item],
        firstItem: item,
        startIndex: currentIndex,
      });
    }
    currentIndex++;
  });

  const handleDragStart = (stackIndex: number) => (e: React.DragEvent) => {
    setDraggedStackIndex(stackIndex);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (stackIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = (targetStackIndex: number) => (e: React.DragEvent) => {
    e.preventDefault();
    if (draggedStackIndex === null || draggedStackIndex === targetStackIndex) {
      setDraggedStackIndex(null);
      return;
    }

    const fromStack = groupedQueue[draggedStackIndex];
    const toStack = groupedQueue[targetStackIndex];

    // Move all items in the dragged stack
    const fromStartIndex = fromStack.startIndex;
    const toStartIndex = toStack.startIndex;

    // Move each item in the stack
    for (let i = 0; i < fromStack.items.length; i++) {
      const adjustedFromIndex = fromStartIndex + (draggedStackIndex < targetStackIndex ? 0 : i);
      const adjustedToIndex = toStartIndex + (draggedStackIndex < targetStackIndex ? 0 : i);
      reorderQueue(adjustedFromIndex, adjustedToIndex);
    }

    setDraggedStackIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedStackIndex(null);
  };

  return (
    <S.Sidebar>
      <S.Header>
        <S.Title>Crafting Queue</S.Title>
      </S.Header>
      <S.QueueList>
        {groupedQueue.length === 0 ? (
          <S.EmptyState>Queue is empty. Add items from the Crafting tab.</S.EmptyState>
        ) : (
          groupedQueue.map((stack, stackIndex) => {
            const recipe = CRAFTING_RECIPES.find(r => r.id === stack.recipeId);
            if (!recipe) return null;

            const currentItem = stack.firstItem;
            const effectiveDuration = recipe.durationMs / CRAFTING_POWER;
            const progress = Math.min(100, (currentItem.progressMs / effectiveDuration) * 100);
            const canDrag = stackIndex > 0; // Can't drag the first item (currently being crafted)
            const isDragging = draggedStackIndex === stackIndex;

            return (
              <S.QueueItem
                key={`stack-${stackIndex}-${currentItem.id}`}
                $warning={currentItem.missingIngredients && stackIndex === 0}
                $isDragging={isDragging}
                draggable={canDrag}
                onDragStart={canDrag ? handleDragStart(stackIndex) : undefined}
                onDragOver={handleDragOver(stackIndex)}
                onDrop={handleDrop(stackIndex)}
                onDragEnd={handleDragEnd}
                onClick={currentItem.missingIngredients && stackIndex === 0
                  ? (e) => {
                      e.stopPropagation();
                      handleWarningClick(currentItem.id, stack.count);
                    }
                  : undefined}
              >
                <S.ItemHeader>
                  <S.DragHandle $disabled={!canDrag}>
                    {canDrag ? '⋮⋮' : ''}
                  </S.DragHandle>
                  {stack.count > 1 && (
                    <S.CountBadge
                      onClick={(e) => handleRemoveOne(stack, e)}
                      title="Click to remove one from queue"
                    >
                      {stack.count}x
                    </S.CountBadge>
                  )}
                  <ItemIcon itemId={recipe.result.itemId} size={28} background="transparent" />
                  <S.ItemName>{recipe.name}</S.ItemName>
                  <S.RemoveButton onClick={(e) => {
                    e.stopPropagation();
                    // Remove all items in this stack
                    stack.items.forEach(item => removeFromQueue(item.id));
                  }} title="Remove all from queue">×</S.RemoveButton>
                </S.ItemHeader>
                <S.ProgressBar>
                  <S.ProgressFill $progress={progress} />
                </S.ProgressBar>
                {currentItem.missingIngredients && stackIndex === 0 && (
                  <S.WarningText>Missing ingredients - Click to queue</S.WarningText>
                )}
              </S.QueueItem>
            );
          })
        )}
      </S.QueueList>
    </S.Sidebar>
  );
}
